'use client'

import * as React from 'react'
import {
  ChangelogEntry,
  changelogEntryFragment,
} from '~/app/(mkt)/changelog/fragments'
import { CalendarIcon } from '@radix-ui/react-icons'

import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'
import { ChangelogSidebarItem } from './sidebar-item'
import { Preferences } from './preferences'
import { useQuery } from '@tanstack/react-query'
import { basehub } from 'basehub'
import { env } from '~/env'

export const ChangelogTab = () => {
  const { data: changelogEntries } = useQuery({
    queryKey: ['editor-changelog'],
    queryFn: async () => {
      const data = await basehub({
        token: env.NEXT_PUBLIC_BASEHUB_TOKEN,
      }).query({
        changelog: {
          entries: {
            __args: {
              orderBy: 'publishDate__DESC',
            },
            items: changelogEntryFragment,
          },
        },
      })
      return data.changelog.entries.items
    },
  })

  const groups = React.useMemo(() => {
    if (!changelogEntries) return []
    const now = new Date()
    const oneMonthAgo = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate()
    ).toISOString()
    const threeMonthsAgo = new Date(
      now.getFullYear(),
      now.getMonth() - 3,
      now.getDate()
    ).toISOString()

    const groups: Record<
      'This month' | 'Past 3 months' | 'Older releases',
      ChangelogEntry[]
    > = {
      'This month': [],
      'Past 3 months': [],
      'Older releases': [],
    }

    changelogEntries.forEach((entry) => {
      const entryDate = entry.publishDate
      if (entryDate >= oneMonthAgo) {
        groups['This month'].push(entry)
      } else if (entryDate >= threeMonthsAgo) {
        groups['Past 3 months'].push(entry)
      } else {
        groups['Older releases'].push(entry)
      }
    })

    return Object.entries(groups)
  }, [changelogEntries])

  return (
    <>
      <h5 className="text-dark-dim text-sm font-medium px-4 mt-3 mb-[7px]">
        Release Notes
      </h5>
      <hr className="border-dark-border-solid mx-4" />
      <ScrollArea className="px-2 h-full">
        {groups
          .filter(([, entries]) => entries.length)
          .map(([group, entries], groupIndex) => (
            <div key={group}>
              <div className="text-dark-muted flex items-center gap-x-2 m-2">
                <CalendarIcon height={15} width={15} />
                <h5 className="text-xs leading-normal">{group}</h5>
              </div>
              {entries.map((entry, index) => (
                <ChangelogSidebarItem
                  key={entry._id}
                  isFirst={!index && !groupIndex}
                  {...entry}
                />
              ))}
            </div>
          ))}
      </ScrollArea>
      <div className="px-3 py-[0.9375rem] border-dark-border-solid border-t">
        <Preferences />
      </div>
    </>
  )
}
